import { AllocatorPageBulkAllocationsQuery, InvestorSortBy, InvestorSortDirection } from '@/__generated__/graphql';
import { IFilterBarData } from '@/components/basicComponents/filterBar';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { CounterTitle, ITab, MainTabsMobile, TabsDetailsTitleMobile, TabsWrapMobile } from '@/components/basicComponents/tabs';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle, PageTitleWrap } from '@/components/header/pageTitle';
import { GET_TOTAL_LIST_INVESTORS_QUERY } from '@/components/investors-page/queries';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import MobileModal from '@/components/mobileModal';
import { SYSTEM_SETTINGS_QUERY } from '@/components/settings-page/queries';
import { Details } from '@/components/strategyDetails-page/details';
import FilterPage from '@/components/table/filterPage';
import { AdditionalFilter, AllocationsFiltersStructure, FiltersItems } from '@/components/table/filterPage/types';
import { createNewFilter } from '@/components/table/filterPage/utils';
import { SORT_ORDER } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import guid from '@/shared/utils/guid';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { SELECTORS } from '@/types/enums';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { ALLOCATION_STATUS, SUITABILITY } from '@trace-8/trace8-schema';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { initialFilterBarValues } from './constants';
import FooterModal from './footer';
import EditAmount from './modals/editAmount';
import SubmitTo from './modals/submitTo';
import {
  ALLOCATOR_PAGE_ADVISORS_QUERY,
  ALLOCATOR_PAGE_BULK_ALLOCATIONS_QUERY,
  ALLOCATOR_PAGE_BULK_ALLOCATIONS_TOTAL_QUERY,
  ALLOCATOR_PAGE_CREATE_ALLOCATIONS_MUTATION,
  ALLOCATOR_PAGE_UPDATE_ALLOCATIONS_MUTATION
} from './queries';
import SuitabilityDetails from './table/cell/suitabilityDetails';
import Table from './table/table';
import { AllocatorPageMode, AllocatorTabsEnum, ExtendedBulkAllocation, GroupedLegalEntityCommittedCapital } from './types';
import { AllocatorZeroState } from './zeroState';

const getAllocationStatusToUpdate = (row: ExtendedBulkAllocation, activeTab: AllocatorTabsEnum, allocationReviewFlag: boolean) => {
  if (!allocationReviewFlag) {
    return row.reason === 'Always Show' || row.decision ? ALLOCATION_STATUS.OUTSTANDING : ALLOCATION_STATUS.INTERNAL_REVIEW_REJECTED;
  }

  switch (activeTab) {
    case AllocatorTabsEnum.BeginAllocation:
      return ALLOCATION_STATUS.INVESTMENT_REVIEW;
    case AllocatorTabsEnum.InvestmentReview:
      return ALLOCATION_STATUS.ADVISOR_REVIEW;
    case AllocatorTabsEnum.AdvisorReview:
      return ALLOCATION_STATUS.COMPLIANCE;
    case AllocatorTabsEnum.ComplianceReview:
      return row.reason === 'Always Show' || row.decision ? ALLOCATION_STATUS.OUTSTANDING : ALLOCATION_STATUS.INTERNAL_REVIEW_REJECTED;
  }
};

const recommendedAllocationsQueryAllocationStatuses = {
  [AllocatorTabsEnum.BeginAllocation]: '',
  [AllocatorTabsEnum.InvestmentReview]: ALLOCATION_STATUS.INVESTMENT_REVIEW,
  [AllocatorTabsEnum.AdvisorReview]: ALLOCATION_STATUS.ADVISOR_REVIEW,
  [AllocatorTabsEnum.ComplianceReview]: ALLOCATION_STATUS.COMPLIANCE
};

const footerMessagesLabels = {
  [AllocatorTabsEnum.BeginAllocation]: 'Submitted To Investment Review',
  [AllocatorTabsEnum.InvestmentReview]: 'Submitted To Advisor Review',
  [AllocatorTabsEnum.AdvisorReview]: 'Submitted To Compliance',
  [AllocatorTabsEnum.ComplianceReview]: 'Allocations Initiated'
};

enum DECISION_STATUS {
  YES = 'Yes',
  SHOW_ALL = 'Show All',
  NO = 'No'
}

export const AllocatorPage = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const params = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { searchParams, onChangeActiveTab, onChangeSearch, onChangeFilterBar, onChangeSort, onChangeToggle, saveScrollPostion, makeScroll } =
    useSearchParamsManager();

  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab = (searchParams.get('activeTab') || AllocatorTabsEnum.BeginAllocation) as AllocatorTabsEnum;
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';
  const q = searchParams.get('q') || '';
  const filterBarParams = searchParams.get('filterBar');
  const key = searchParams.get('key') || 'entity';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const position = searchParams.get('position') || 0;

  const [allocationReviewFlag, setAllocationReviewFlag] = useState(false);
  const [tabs, setTabs] = useState<ITab[]>([]);
  const [filterBar, setFilterBar] = useState<IFilterBarData[]>(initialFilterBarValues);
  const [additionalFilters, setAdditionalFilters] = useState<AdditionalFilter[]>([createNewFilter('Select A Filter Option')]);
  const [filtersItems, setFiltersItems] = useState<FiltersItems>({
    advisors: [createSimpleSelectOption(SELECTORS.ALL_ADVISORS)],
    criteria: [createSimpleSelectOption(SELECTORS.ANY_CRITERIA), ...Object.values(SUITABILITY).map((value) => createSimpleSelectOption(value))],
    decision: [createSimpleSelectOption(SELECTORS.ANY_DECISION), ...Object.values(DECISION_STATUS).map((value) => createSimpleSelectOption(value))]
  });
  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);
  const [mode, setMode] = useState<AllocatorPageMode>(AllocatorPageMode.VIEW);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [investmentData, setInvestmentData] = useState<AllocatorPageBulkAllocationsQuery['bulkAllocations']['investment'] | null>(null);
  const [bulkAllocations, setBulkAllocations] = useState<ExtendedBulkAllocation[]>([]);
  const [currentRow, setCurrentRow] = useState<ExtendedBulkAllocation | null>(null);
  const [isSuitabilityOpen, setIsSuitabilityOpen] = useState(false);
  const [suitabilityData, setSuitabilityData] = useState<ExtendedBulkAllocation>();
  const [changedAllocations, setChangedAllocations] = useState<ExtendedBulkAllocation[]>([]);
  const [changedGroupedLegalEntitiesCommittedCapital, setChangedGroupedLegalEntitiesCommittedCapital] = useState<
    Record<string, GroupedLegalEntityCommittedCapital>
  >({});
  const [footerMessages, setFooterMessages] = useState<{ greenMessage: string; redMessage: string }>({ greenMessage: '', redMessage: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createAllocations] = useMutation(ALLOCATOR_PAGE_CREATE_ALLOCATIONS_MUTATION);
  const [updateAllocations] = useMutation(ALLOCATOR_PAGE_UPDATE_ALLOCATIONS_MUTATION);

  useEffect(() => {
    if (!filterBarParams) return;

    const matchedFilterBarValues = initialFilterBarValues.map((item) => {
      const matchedFilterItem = JSON.parse(filterBarParams).find((filterItem: string) => filterItem === item.value);

      return {
        ...item,
        enabled: Boolean(matchedFilterItem)
      };
    });
    setFilterBar(matchedFilterBarValues);
  }, []);

  useEffect(() => {
    if (location.state?.changedAllocations) {
      const savedChangedAllocations: ExtendedBulkAllocation[] = location?.state?.changedAllocations || [];
      setChangedAllocations(savedChangedAllocations);

      const savedMode: AllocatorPageMode = location?.state?.mode || AllocatorPageMode.VIEW;
      setMode(savedMode);

      const savedAdditionalFilters = location?.state?.additionalFilters;
      if (savedAdditionalFilters) {
        setAdditionalFilters(savedAdditionalFilters);
      }

      const savedChangedGroupedLegalEntitiesCommittedCapital = location?.state?.changedGroupedLegalEntitiesCommittedCapital;
      if (savedChangedGroupedLegalEntitiesCommittedCapital) {
        setChangedGroupedLegalEntitiesCommittedCapital(savedChangedGroupedLegalEntitiesCommittedCapital);
      }

      const newUrl = location.search ? `.${location.search}` : '.';
      navigate(newUrl, {
        replace: true,
        state: null
      });
    }
  }, [location.state?.changedAllocations]);

  const unitedFilters = additionalFilters.reduce<AllocationsFiltersStructure>(
    (acc, { selected, operator, typeFilter }) => {
      return acc[typeFilter as keyof AllocationsFiltersStructure]
        ? {
            ...acc,
            [typeFilter]: [
              ...acc[typeFilter as keyof AllocationsFiltersStructure],
              {
                selected,
                operator
              }
            ]
          }
        : acc;
    },
    {
      advisors: [],
      criteria: [],
      decision: []
    }
  );

  const { loading: internalReviewFeatureFlagLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    skip: !user?.tenant?.id,
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'featureFlag',
        key: 'internalReview',
        allowNullTenantSettings: true
      }
    },
    onCompleted: (data) => {
      setAllocationReviewFlag(data?.systemSettings?.find((setting) => setting?.key === 'internalReview')?.value === 'true' || false);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: getTotalListInvestorsData, loading: getTotalListInvestorsLoading } = useQuery(GET_TOTAL_LIST_INVESTORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        sortBy: InvestorSortBy.Investor,
        sortDirection: InvestorSortDirection.Asc,
        limit: 1,
        includeRecommendations: true
      }
    }
  });

  const { loading: advisorsLoading } = useQuery(ALLOCATOR_PAGE_ADVISORS_QUERY, {
    onCompleted: (data) =>
      setFiltersItems({
        ...filtersItems,
        advisors: [
          createSimpleSelectOption(SELECTORS.ALL_ADVISORS),
          ...data.Advisors.map(({ id, firstName, lastName, initials }) =>
            createSimpleSelectOption(id, initials ? initials : `${firstName} ${lastName}`)
          )
        ]
      })
  });

  const {
    data: beginAllocationTotalData,
    loading: beginAllocationTotalLoading,
    networkStatus: beginAllocationTotalNetworkStatus,
    refetch: beginAllocationTotalRefetch
  } = useQuery(ALLOCATOR_PAGE_BULK_ALLOCATIONS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.id,
    variables: {
      input: {
        investmentId: params?.id,
        potentialAllocations: true,
        allocationStatusFilter: { initialAllocation: true, alreadyCommitted: true, internalReviewRejections: true, terminated: true },
        includeProposedInvestments: toggle
      }
    }
  });

  const {
    data: investmentReviewTabTotalData,
    loading: investmentReviewTabTotalLoading,
    networkStatus: investmentReviewTabTotalNetworkStatus,
    refetch: investmentReviewTabTotalRefetch
  } = useQuery(ALLOCATOR_PAGE_BULK_ALLOCATIONS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.id || !allocationReviewFlag,
    variables: {
      input: { investmentId: params?.id, includeProposedInvestments: toggle, allocationStatus: ALLOCATION_STATUS.INVESTMENT_REVIEW }
    }
  });

  const {
    data: advisorReviewTabTotalData,
    loading: advisorReviewTabTotalLoading,
    networkStatus: advisorReviewTabTotalNetworkStatus,
    refetch: advisorReviewTabTotalRefetch
  } = useQuery(ALLOCATOR_PAGE_BULK_ALLOCATIONS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.id || !allocationReviewFlag,
    variables: {
      input: {
        investmentId: params?.id,
        includeProposedInvestments: toggle,
        allocationStatus: ALLOCATION_STATUS.ADVISOR_REVIEW
      }
    }
  });

  const {
    data: complianceReviewTabTotalData,
    loading: complianceReviewTabTotalLoading,
    networkStatus: complianceReviewTabTotalNetworkStatus,
    refetch: complianceReviewTabTotalRefetch
  } = useQuery(ALLOCATOR_PAGE_BULK_ALLOCATIONS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.id || !allocationReviewFlag,
    variables: {
      input: {
        investmentId: params?.id,
        includeProposedInvestments: toggle,
        allocationStatus: ALLOCATION_STATUS.COMPLIANCE
      }
    }
  });

  const {
    data: bulkAllocationsData,
    loading: bulkAllocationsLoading,
    networkStatus: bulkAllocationsNetworkStatus,
    refetch: bulkAllocationsRefetch
  } = useQuery(ALLOCATOR_PAGE_BULK_ALLOCATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.id,
    variables: {
      input: {
        investmentId: params?.id,
        search: q.toLowerCase().trim(),
        sort: key,
        order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        includeProposedInvestments: toggle,
        potentialAllocations: activeTab === AllocatorTabsEnum.BeginAllocation,
        allocationStatus: recommendedAllocationsQueryAllocationStatuses[activeTab],
        ...(activeTab === AllocatorTabsEnum.BeginAllocation && {
          allocationStatusFilter: filterBar.every((el: { enabled: boolean }) => !el.enabled)
            ? { initialAllocation: true, alreadyCommitted: true, internalReviewRejections: true, terminated: true }
            : {
                initialAllocation: filterBar.find((filter) => filter.value === 'initialAllocation')?.enabled,
                alreadyCommitted: filterBar.find((filter) => filter.value === 'alreadyCommitted')?.enabled,
                internalReviewRejections: filterBar.find((filter) => filter.value === 'internalReviewRejections')?.enabled,
                terminated: filterBar.find((filter) => filter.value === 'terminated')?.enabled
              }
        }),
        additionalAdvisorFilters: unitedFilters.advisors,
        additionalDecisionFilters: unitedFilters.decision,
        additionalSuitabilityFilters: unitedFilters.criteria
      }
    }
  });

  const getDecisionReason = (allocation: AllocatorPageBulkAllocationsQuery['bulkAllocations']['allocations'][number]) => {
    if (!allocation.decision && allocation.reason === 'Always Show') {
      return 'Show All';
    }

    if (allocation.decision && allocation.reason === 'Always Show') {
      return 'Yes';
    }

    return `${allocation.decision ? 'Yes' : 'No'}${allocation.reason ? ` - ${allocation.reason}` : ''}`;
  };

  const getReason = (allocation: AllocatorPageBulkAllocationsQuery['bulkAllocations']['allocations'][number]) => {
    if (allocation.decision && allocation.reason === 'Always Show') {
      return '';
    }

    return allocation.reason;
  };

  useEffect(() => {
    if (bulkAllocationsLoading) return;

    const { investment, allocations, filters } = bulkAllocationsData?.bulkAllocations || {};

    setInvestmentData(investment || null);
    if (filterBar.every((el: { enabled: boolean }) => !el.enabled)) {
      const disabledFilters = filters?.map((filter) => ({
        ...filter,
        enabled: false
      }));
      setFilterBar((disabledFilters as IFilterBarData[]) || []);
    } else {
      setFilterBar((filters as IFilterBarData[]) || []);
    }

    const updatedBulkAllocations = allocations?.map((allocation) => {
      const changedAllocation = changedAllocations.find((changedAllocation) => changedAllocation.legalEntity.id === allocation.legalEntity.id);
      if (changedAllocation) {
        return changedAllocation;
      }

      const decisionReason = getDecisionReason(allocation);
      const reason = getReason(allocation);

      return {
        ...allocation,
        checked: false,
        reason,
        decisionReason,
        selectedGroupedLegalEntity:
          allocation?.group && !!allocation?.group?.legalEntities?.length
            ? {
                id: allocation?.legalEntity.id,
                entityName: allocation?.legalEntity.entityName,
                committedCapital: allocation?.committedCapital,
                proposalType: allocation?.proposalType
              }
            : null
      } as ExtendedBulkAllocation;
    });

    setBulkAllocations(updatedBulkAllocations || []);
  }, [bulkAllocationsLoading, bulkAllocationsData]);

  const loadingStates = [
    internalReviewFeatureFlagLoading,
    getTotalListInvestorsLoading,
    advisorsLoading,
    bulkAllocationsLoading,
    beginAllocationTotalLoading,
    investmentReviewTabTotalLoading,
    advisorReviewTabTotalLoading,
    complianceReviewTabTotalLoading
  ];

  const isLoading = loadingStates.some((loading) => loading);

  useEffect(() => {
    if (!isLoading && position) {
      makeScroll(position);
    }
  }, [isLoading, position]);

  useEffect(() => {
    const isLoading =
      internalReviewFeatureFlagLoading ||
      beginAllocationTotalLoading ||
      investmentReviewTabTotalLoading ||
      advisorReviewTabTotalLoading ||
      complianceReviewTabTotalLoading;
    if (isLoading) return;

    const beginAllocationAmount = beginAllocationTotalData?.bulkAllocations?.total || 0;
    const investmentReviewTabAmount = investmentReviewTabTotalData?.bulkAllocations?.total || 0;
    const advisorReviewTabAmount = advisorReviewTabTotalData?.bulkAllocations?.total || 0;
    const complianceReviewTabAmount = complianceReviewTabTotalData?.bulkAllocations?.total || 0;

    if (allocationReviewFlag) {
      setTabs([
        { value: AllocatorTabsEnum.BeginAllocation, amount: beginAllocationAmount },
        { value: AllocatorTabsEnum.InvestmentReview, amount: investmentReviewTabAmount },
        { value: AllocatorTabsEnum.AdvisorReview, amount: advisorReviewTabAmount },
        { value: AllocatorTabsEnum.ComplianceReview, amount: complianceReviewTabAmount }
      ]);
      return;
    }

    setTabs([{ value: AllocatorTabsEnum.BeginAllocation, amount: beginAllocationAmount }]);
  }, [
    allocationReviewFlag,
    internalReviewFeatureFlagLoading,
    beginAllocationTotalLoading,
    investmentReviewTabTotalLoading,
    advisorReviewTabTotalLoading,
    complianceReviewTabTotalLoading
  ]);

  const refetchTotalQueries = async () => {
    const refetchMap = {
      [AllocatorTabsEnum.BeginAllocation]: [beginAllocationTotalRefetch, investmentReviewTabTotalRefetch],
      [AllocatorTabsEnum.InvestmentReview]: [investmentReviewTabTotalRefetch, advisorReviewTabTotalRefetch],
      [AllocatorTabsEnum.AdvisorReview]: [advisorReviewTabTotalRefetch, complianceReviewTabTotalRefetch],
      [AllocatorTabsEnum.ComplianceReview]: [complianceReviewTabTotalRefetch]
    };

    const refetchFunctions = refetchMap[activeTab];
    if (refetchFunctions) {
      for await (const refetch of refetchFunctions) {
        await refetch();
      }
    }
  };

  const clearFooterSuccessMessages = () => {
    if (footerMessages.greenMessage || footerMessages.redMessage) {
      setFooterMessages({ greenMessage: '', redMessage: '' });
    }
  };

  const selectRow = (row: ExtendedBulkAllocation) => {
    clearFooterSuccessMessages();

    setBulkAllocations((prevList) => {
      const updatedList = prevList.map((item) => (item.legalEntity.id === row.legalEntity.id ? { ...item, checked: !item.checked } : item));
      const isAllocationChanged = changedAllocations.some((changedAllocationRow) => changedAllocationRow.legalEntity?.id === row.legalEntity?.id);

      const updatedChosenRows = isAllocationChanged
        ? changedAllocations.map((item) => (item.legalEntity.id === row.legalEntity.id ? { ...item, checked: !item.checked } : item))
        : [...changedAllocations, { ...row, checked: !row.checked }];

      setChangedAllocations(updatedChosenRows);

      return updatedList;
    });
  };

  const selectAllRows = (isChecked: boolean) => {
    clearFooterSuccessMessages();

    setBulkAllocations((prevList) => {
      const updatedList = prevList.map((item) => (item.editable ? { ...item, checked: isChecked } : item));

      const editableRows = prevList.filter((item) => item.editable);
      const updatedChangedRows = editableRows.map((item) => {
        const currentChangedAllocation = changedAllocations.find(
          (changedAllocationRow) => changedAllocationRow.legalEntity?.id === item.legalEntity?.id
        );

        if (currentChangedAllocation) {
          return {
            ...currentChangedAllocation,
            checked: isChecked
          };
        }

        return { ...item, checked: isChecked };
      });
      setChangedAllocations(updatedChangedRows);

      return updatedList;
    });
  };

  const updateDecisionReason = (id: string, value: string) => {
    const currentRow = bulkAllocations.find((row) => row.legalEntity?.id === id);

    if (currentRow && currentRow.decisionReason === value) return;
    const updatedCurrentRow = {
      ...currentRow,
      decisionReason: value,
      decision: (value as string).split(' - ')[0] === 'Yes',
      reason: value === 'Show All' ? 'Always Show' : (value as string).split(' - ')[1] || ''
    };

    const isAllocationChanged = changedAllocations.some((row) => row.legalEntity?.id === updatedCurrentRow.legalEntity?.id);

    setBulkAllocations(bulkAllocations.map((row) => (row.legalEntity?.id === id ? updatedCurrentRow : row)));
    setChangedAllocations(
      isAllocationChanged
        ? changedAllocations.map((row) => (row.legalEntity?.id === updatedCurrentRow.legalEntity?.id ? updatedCurrentRow : row))
        : [...changedAllocations, updatedCurrentRow]
    );
  };

  const updateUngroupedEntityAmountInState = ({ entityId, committedCapital }: { entityId: string; committedCapital: number }) => {
    setBulkAllocations((prevBulkAllocations) =>
      prevBulkAllocations.map((row) => (row.legalEntity?.id === entityId ? { ...row, committedCapital } : row))
    );

    const currentRow = bulkAllocations.find((row) => row.legalEntity?.id === entityId);

    setChangedAllocations((prevChangedAllocations) => {
      const isAllocationChanged = prevChangedAllocations.some((row) => row.legalEntity?.id === currentRow.legalEntity?.id);

      return isAllocationChanged
        ? prevChangedAllocations.map((row) => (row.legalEntity?.id === currentRow.legalEntity?.id ? { ...currentRow, committedCapital } : row))
        : [...prevChangedAllocations, { ...currentRow, committedCapital }];
    });
  };

  const onChangeGroupedEntityAndAmountInState = ({
    groupId,
    groupedEntityId,
    committedCapital
  }: {
    groupId: string;
    groupedEntityId: string;
    committedCapital: number;
  }) => {
    let updatedBulkAllocationRow: ExtendedBulkAllocation = null;

    setChangedGroupedLegalEntitiesCommittedCapital((prev) => {
      return {
        ...prev,
        [groupId]: { groupedEntityId, committedCapital }
      };
    });

    const updatedBulkAllocations = bulkAllocations.map((row) => {
      if (row.legalEntity?.groupId === groupId) {
        const groupedLegalEntity = row?.group?.legalEntities?.find((entity) => entity.id === groupedEntityId);
        if (groupedLegalEntity) {
          const updatedRow = {
            ...row,
            committedCapital,
            selectedGroupedLegalEntity: {
              id: groupedLegalEntity.id,
              entityName: groupedLegalEntity.entityName,
              committedCapital: groupedLegalEntity?.committedCapital,
              proposalType: groupedLegalEntity?.proposalType
            }
          };
          updatedBulkAllocationRow = updatedRow;
          return updatedRow;
        }
        return row;
      }
      return row;
    });
    setBulkAllocations(updatedBulkAllocations);

    if (updatedBulkAllocationRow) {
      setChangedAllocations((prevChangedAllocations) => {
        const isAllocationChanged = prevChangedAllocations.some((row) => row.legalEntity?.groupId === groupId);

        if (!isAllocationChanged) {
          return [...prevChangedAllocations, updatedBulkAllocationRow];
        }
        return prevChangedAllocations.map((row) =>
          row.legalEntity?.groupId === groupId
            ? {
                ...row,
                committedCapital,
                selectedGroupedLegalEntity: {
                  id: updatedBulkAllocationRow?.selectedGroupedLegalEntity?.id,
                  entityName: updatedBulkAllocationRow?.selectedGroupedLegalEntity?.entityName,
                  committedCapital: updatedBulkAllocationRow?.selectedGroupedLegalEntity?.committedCapital,
                  proposalType: updatedBulkAllocationRow?.selectedGroupedLegalEntity?.proposalType
                }
              }
            : row
        );
      });
      return;
    }
  };

  const openModalWindow = (type: string, row?: ExtendedBulkAllocation) => {
    setModalWindow({ type, isOpen: true });
    if (row) {
      setCurrentRow(row);
    }
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentRow(null);
  };

  const openMobileModal = (data: ExtendedBulkAllocation, modalType: string) => {
    if (modalType === 'suitability') {
      setIsSuitabilityOpen(true);
      setSuitabilityData(data);
    }
  };

  const createDataToUpdate = () => {
    const chosenRows = changedAllocations.filter((row) => row.checked);
    return chosenRows.map((row) => ({
      ...(activeTab !== AllocatorTabsEnum.BeginAllocation && row.id && { id: row.id }),
      ...(activeTab === AllocatorTabsEnum.BeginAllocation && { investmentId: row.investment.id }),
      legalEntityId: row?.selectedGroupedLegalEntity ? row.selectedGroupedLegalEntity.id : row.legalEntity.id,
      committedCapital: row.committedCapital,
      proposalType: row.proposalType,
      decision: row.decision,
      reason: row.reason,
      suitability: row.suitability,
      suitabilityCriteria: row?.suitabilityCriteria?.map(({ label, value }) => ({ label, value })) || [],
      allocationStatus: getAllocationStatusToUpdate(row, activeTab, allocationReviewFlag)
    }));
  };

  const handleCreateAllocations = async () => {
    try {
      setIsSubmitting(true);

      const data = createDataToUpdate();
      if (!data.length) return;

      await createAllocations({
        variables: {
          data
        }
      });

      setChangedAllocations([]);
      await refetchTotalQueries();
      await bulkAllocationsRefetch();

      if (allocationReviewFlag) {
        const newActiveTabIndex = Object.values(AllocatorTabsEnum).findIndex((tab) => tab === activeTab) + 1;
        const newActiveTab = Object.values(AllocatorTabsEnum)[newActiveTabIndex];
        if (newActiveTab) {
          onChangeActiveTab(newActiveTab, activeTab);
          setBulkAllocations([]);
        }
      }

      if (activeTab !== AllocatorTabsEnum.ComplianceReview && allocationReviewFlag) {
        setFooterMessages({ greenMessage: `${data.length} ${footerMessagesLabels[activeTab]}`, redMessage: '' });
        return;
      }

      const initiatedAllocations = data.filter((row) => row.reason === 'Always Show' || row.decision).length;
      const internalReviewRejectedAllocations = data.length - initiatedAllocations;

      setFooterMessages({
        greenMessage: initiatedAllocations > 0 ? `${initiatedAllocations} Allocations Initiated` : '',
        redMessage: internalReviewRejectedAllocations > 0 ? `${internalReviewRejectedAllocations} Internal Review Rejected` : ''
      });
    } catch (error) {
      console.error(error, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAllocations = async () => {
    try {
      setIsSubmitting(true);

      const data = createDataToUpdate();
      if (!data.length) return;

      await updateAllocations({
        variables: {
          data
        }
      });

      setChangedAllocations([]);
      await refetchTotalQueries();
      await bulkAllocationsRefetch();

      if (allocationReviewFlag) {
        const newActiveTabIndex = Object.values(AllocatorTabsEnum).findIndex((tab) => tab === activeTab) + 1;
        const newActiveTab = Object.values(AllocatorTabsEnum)[newActiveTabIndex];
        if (newActiveTab) {
          onChangeActiveTab(newActiveTab, activeTab);
          setBulkAllocations([]);
        }
      }

      if (activeTab !== AllocatorTabsEnum.ComplianceReview && allocationReviewFlag) {
        setFooterMessages({ greenMessage: `${data.length} ${footerMessagesLabels[activeTab]}`, redMessage: '' });
        return;
      }

      const initiatedAllocations = data.filter((row) => row.reason === 'Always Show' || row.decision).length;
      const internalReviewRejectedAllocations = data.length - initiatedAllocations;

      setFooterMessages({
        greenMessage: initiatedAllocations > 0 ? `${initiatedAllocations} Allocations Initiated` : '',
        redMessage: internalReviewRejectedAllocations > 0 ? `${internalReviewRejectedAllocations} Internal Review Rejected` : ''
      });
    } catch (error) {
      console.error(error, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitTo = () => {
    if (allocationReviewFlag) {
      if (activeTab === AllocatorTabsEnum.ComplianceReview) {
        openModalWindow('compliance-review-submit-to');
        return;
      }
      if (activeTab === AllocatorTabsEnum.BeginAllocation) {
        handleCreateAllocations();
      } else {
        handleUpdateAllocations();
      }
      return;
    }
    openModalWindow('begin-allocation-submit-to');
  };

  const getSummary = () => {
    const initialSummary = {
      count: 0,
      yesCount: 0,
      totalAmount: 0
    };
    let footerData = undefined;

    footerData = {
      count: bulkAllocations.filter((allocation) => allocation.editable).length,
      yesCount: bulkAllocations.filter((allocation) => allocation.decision && allocation.reason !== 'Always Show').length,
      totalAmount: bulkAllocations
        .filter((allocation) => allocation.decision && allocation.reason !== 'Always Show')
        .reduce((sum: number, allocation) => sum + allocation.committedCapital, 0)
    };

    return footerData || initialSummary;
  };

  const preservePageState = () => {
    saveScrollPostion();

    const currentSearchParams = searchParams.toString();
    const newUrl = currentSearchParams ? `.?${currentSearchParams}` : '.';
    navigate(newUrl, {
      replace: true,
      state: {
        mode,
        changedAllocations,
        additionalFilters,
        changedGroupedLegalEntitiesCommittedCapital,
        searchParams: currentSearchParams
      }
    });
  };

  const handleFilterBar = (filterBarItems: IFilterBarData[]) => {
    setFilterBar(filterBarItems);
    onChangeFilterBar(filterBarItems);
  };

  const handleChangeMode = (mode: AllocatorPageMode) => {
    setMode(mode);
    clearFooterSuccessMessages();
  };

  const handleChangeActiveTab = (value: string) => {
    clearFooterSuccessMessages();

    if (activeTab === value) return;
    onChangeActiveTab(value, activeTab);
    setBulkAllocations([]);
    setChangedAllocations([]);
    if (isMobile) {
      setIsTabMenuOpen(false);
    }
  };

  const getSelectedRows = useMemo(() => {
    return changedAllocations.filter((row) => row.checked);
  }, [changedAllocations]);

  const isTableLoading = bulkAllocationsNetworkStatus === NetworkStatus.loading || bulkAllocationsNetworkStatus === NetworkStatus.setVariables;

  const isLoadingOnWholeScreen =
    internalReviewFeatureFlagLoading ||
    getTotalListInvestorsLoading ||
    advisorsLoading ||
    beginAllocationTotalNetworkStatus === NetworkStatus.loading ||
    investmentReviewTabTotalNetworkStatus === NetworkStatus.loading ||
    advisorReviewTabTotalNetworkStatus === NetworkStatus.loading ||
    complianceReviewTabTotalNetworkStatus === NetworkStatus.loading;

  if (isLoadingOnWholeScreen) {
    return <LoaderOnWholeScreen />;
  }

  if (getTotalListInvestorsData?.listInvestors?.total === 0) {
    return <AllocatorZeroState />;
  }

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent
          isOpen={true}
          onClose={() => {}}
          title="Please Wait"
          showCloseIcon={false}
          showCancelButton={false}
          isUseOutsideClick={false}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {isSuitabilityOpen && suitabilityData && (
        <MobileModal
          title={suitabilityData.suitability}
          onClose={() => {
            setIsSuitabilityOpen(false);
          }}
          onClick={() => null}
        >
          <SuitabilityDetails data={suitabilityData?.suitabilityCriteria || []} key={guid()} />
        </MobileModal>
      )}
      {modalWindow.isOpen && modalWindow.type === 'begin-allocation-submit-to' && (
        <SubmitTo
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          changedAllocations={changedAllocations}
          updateAllocationRequest={handleCreateAllocations}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'compliance-review-submit-to' && (
        <SubmitTo
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          changedAllocations={changedAllocations}
          updateAllocationRequest={handleUpdateAllocations}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'edit-amount' && (
        <EditAmount
          isOpen={modalWindow.isOpen}
          row={currentRow}
          changedGroupedLegalEntitiesCommittedCapital={changedGroupedLegalEntitiesCommittedCapital}
          updateUngroupedEntityAmountInState={updateUngroupedEntityAmountInState}
          onChangeGroupedEntityAndAmountInState={onChangeGroupedEntityAndAmountInState}
          bulkAllocationsRefetch={bulkAllocationsRefetch}
          onClose={closeModalWindow}
        />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/dealCatalog' })} backToTitle={backToTitle} />} />
        {investmentData?.managerId && <ManagerNameWrap withBorder>Manager: {investmentData.managerId}</ManagerNameWrap>}
        <PageTitle title={investmentData?.name || ''} />
        <Details
          investmentId={params?.id}
          tabs={tabs}
          activeTab={activeTab}
          isTabMenuOpen={isTabMenuOpen}
          showMinimalData={false}
          showGreyTabs={false}
          showInfoTags={true}
          tabsPlacement="bottom"
          setIsTabMenuOpen={setIsTabMenuOpen}
          setActiveTab={handleChangeActiveTab}
        />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          {isTabMenuOpen && isMobile && (
            <MainTabsMobile>
              {tabs.map((tab, tabId) => (
                <TabsWrapMobile key={tab.value + tabId} onClick={() => handleChangeActiveTab(tab.value)} isActive={activeTab === tab.value}>
                  {'amount' in tab && tab?.amount !== null && <CounterTitle isActive={activeTab === tab.value}>{tab.amount}</CounterTitle>}
                  <TabsDetailsTitleMobile isActive={activeTab === tab.value}>{tab.value}</TabsDetailsTitleMobile>
                </TabsWrapMobile>
              ))}
            </MainTabsMobile>
          )}
          {!isTabMenuOpen && (
            <>
              <FilterPage
                search={{
                  value: q,
                  onChange: onChangeSearch,
                  placeholder: 'Search for an investor'
                }}
                toggle={{
                  title: 'Include Pending Commitments',
                  isActive: toggle,
                  onToggle: () => onChangeToggle(toggle, { shouldSaveScrollPosition: false })
                }}
                additionalFilters={{
                  filtersItems,
                  filters: additionalFilters,
                  applyFilters: setAdditionalFilters,
                  amountResults: bulkAllocationsData?.bulkAllocations?.total || 0,
                  showChosenFilters: false,
                  onRefetch: bulkAllocationsRefetch
                }}
                resultsValue={bulkAllocationsData?.bulkAllocations?.total || 0}
                refetch={bulkAllocationsRefetch}
                {...(activeTab === AllocatorTabsEnum.BeginAllocation && {
                  filterBar: filterBar,
                  onChangeFilterBar: handleFilterBar
                })}
                useNewFilters
              />
              <Table
                sort={{ key, asc }}
                bulkAllocations={bulkAllocations}
                loading={isTableLoading}
                mode={mode}
                investmentName={investmentData?.name}
                forceRecalculateTableFiltersHeight={{ additionalFilters, filterBar, activeTab }}
                setSort={onChangeSort}
                handleChangeMode={handleChangeMode}
                selectAllRows={selectAllRows}
                selectRow={selectRow}
                openModalWindow={openModalWindow}
                updateDecisionReason={updateDecisionReason}
                openMobileModal={openMobileModal}
                preservePageState={preservePageState}
              />
            </>
          )}
        </PaddingWrap>
      </MainWrap>
      {!isMobile && (
        <FooterWrap>
          <FooterModal
            mode={mode}
            summary={getSummary()}
            selectedRows={getSelectedRows}
            allocationReviewFlag={allocationReviewFlag}
            footerMessages={footerMessages}
            handleChangeMode={handleChangeMode}
            handleSubmitTo={handleSubmitTo}
          />
        </FooterWrap>
      )}
    </>
  );
};

const ManagerNameWrap = styled(PageTitleWrap)`
  font-size: 18px;
  font-weight: 600;
  line-height: 18px;
  color: ${themeVar('font.strong')};
`;

const FooterWrap = styled.div`
  margin-top: 150px;
`;
